import type { SupabaseClient } from "@supabase/supabase-js";

export type CoachIdentityProvider = "email" | "google" | "apple" | string;

export interface CoachLinkedIdentity {
  id: string | null;
  provider: CoachIdentityProvider;
  email: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
}

type RawIdentity = {
  id?: string | null;
  provider?: string | null;
  email?: string | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  identity_data?: {
    email?: string | null;
  } | null;
};

export interface CoachMergeCode {
  mergeCode: string;
  expiresAt: string;
  sourceEmail: string | null;
  sourceProviders: string[];
}

export interface CoachMergeResult {
  sourceUserId: string;
  sourceEmail: string | null;
  targetUserId: string;
  targetEmail: string | null;
  sourceProviders: string[];
  relinkProviders: Array<"google" | "apple">;
  relinkReady: boolean;
  warning: string | null;
  movedCollaborations: number;
  movedAnnotations: number;
  movedAssignments: number;
  movedTemplates: number;
  movedNotificationDispatches: number;
  movedBillingEvents: number;
  movedAiRuns: number;
  subscriptionAction: string;
  notificationPreferencesAction: string;
}

export async function listCoachLinkedIdentities(
  client: SupabaseClient,
): Promise<CoachLinkedIdentity[]> {
  const auth = client.auth as {
    getUserIdentities?: () => Promise<{
      data?: { identities?: RawIdentity[] | null } | null;
      error?: { message?: string | null } | null;
    }>;
    getUser: () => Promise<{
      data: { user: { identities?: RawIdentity[] | null } | null };
      error: { message?: string | null } | null;
    }>;
  };

  let identities: RawIdentity[] | null | undefined;
  if (typeof auth.getUserIdentities === "function") {
    const { data, error } = await auth.getUserIdentities();
    if (error) throw new Error(error.message ?? "Could not load linked identities.");
    identities = data?.identities ?? null;
  }

  if (!identities) {
    const { data, error } = await auth.getUser();
    if (error) throw new Error(error.message ?? "Could not load current user.");
    identities = data.user?.identities ?? [];
  }

  return [...(identities ?? [])]
    .map(normalizeIdentity)
    .sort((left, right) => providerSortOrder(left.provider) - providerSortOrder(right.provider));
}

export async function createCoachMergeCode(
  client: SupabaseClient,
): Promise<CoachMergeCode> {
  const { data, error } = await client.functions.invoke<{
    ok: boolean;
    merge_code?: string;
    expires_at?: string;
    source_email?: string | null;
    source_providers?: unknown;
    error?: string;
  }>("coach-account-merge", {
    body: { action: "create_code" },
  });

  if (error) throw new Error(error.message);
  if (!data?.ok || !data.merge_code || !data.expires_at) {
    throw new Error(data?.error ?? "Merge code unavailable.");
  }

  return {
    mergeCode: data.merge_code,
    expiresAt: data.expires_at,
    sourceEmail: data.source_email ?? null,
    sourceProviders: normalizeProviders(data.source_providers),
  };
}

export async function redeemCoachMergeCode(
  client: SupabaseClient,
  mergeCode: string,
): Promise<CoachMergeResult> {
  const { data, error } = await client.functions.invoke<{
    ok: boolean;
    warning?: string | null;
    relink_ready?: boolean;
    merge?: {
      source_user_id?: string;
      source_email?: string | null;
      target_user_id?: string;
      target_email?: string | null;
      source_providers?: unknown;
      relink_providers?: unknown;
      moved_collaborations?: number;
      moved_annotations?: number;
      moved_assignments?: number;
      moved_templates?: number;
      moved_notification_dispatches?: number;
      moved_billing_events?: number;
      moved_ai_runs?: number;
      subscription_action?: string;
      notification_preferences_action?: string;
    } | null;
    error?: string;
  }>("coach-account-merge", {
    body: {
      action: "redeem_code",
      merge_code: mergeCode,
    },
  });

  if (error) throw new Error(error.message);
  if (!data?.ok || !data.merge?.source_user_id || !data.merge?.target_user_id) {
    throw new Error(data?.error ?? "Merge result unavailable.");
  }

  return {
    sourceUserId: data.merge.source_user_id,
    sourceEmail: data.merge.source_email ?? null,
    targetUserId: data.merge.target_user_id,
    targetEmail: data.merge.target_email ?? null,
    sourceProviders: normalizeProviders(data.merge.source_providers),
    relinkProviders: normalizeProviders(data.merge.relink_providers).filter(
      (provider): provider is "google" | "apple" =>
        provider === "google" || provider === "apple",
    ),
    relinkReady: Boolean(data.relink_ready ?? false),
    warning: data.warning ?? null,
    movedCollaborations: Number(data.merge.moved_collaborations ?? 0),
    movedAnnotations: Number(data.merge.moved_annotations ?? 0),
    movedAssignments: Number(data.merge.moved_assignments ?? 0),
    movedTemplates: Number(data.merge.moved_templates ?? 0),
    movedNotificationDispatches: Number(
      data.merge.moved_notification_dispatches ?? 0,
    ),
    movedBillingEvents: Number(data.merge.moved_billing_events ?? 0),
    movedAiRuns: Number(data.merge.moved_ai_runs ?? 0),
    subscriptionAction: data.merge.subscription_action ?? "none",
    notificationPreferencesAction:
      data.merge.notification_preferences_action ?? "none",
  };
}

function normalizeIdentity(identity: RawIdentity): CoachLinkedIdentity {
  return {
    id: identity.id ?? null,
    provider: normalizeProvider(identity.provider),
    email:
      identity.email?.trim() ??
      identity.identity_data?.email?.trim() ??
      null,
    createdAt: identity.created_at ?? null,
    lastSignInAt: identity.last_sign_in_at ?? null,
  };
}

function normalizeProviders(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(
    value
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => normalizeProvider(entry))
      .filter((entry) => entry.length > 0),
  )];
}

function normalizeProvider(value?: string | null): CoachIdentityProvider {
  return value?.trim().toLowerCase() || "unknown";
}

function providerSortOrder(provider: CoachIdentityProvider): number {
  switch (provider) {
    case "email":
      return 0;
    case "google":
      return 1;
    case "apple":
      return 2;
    default:
      return 3;
  }
}
